import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { getNotificationLink } from "../../layout/header/notifications/notification-box/notification-helpers";
import { useNotificationClick } from "../hooks";
import { ListNotificationBase } from "../shared/ListNotificationBase";
import { CONTENT_NOTIFICATION_CONFIG } from "../config/notificationConfigs";

/**
 * ListContentNotification component for content-based notifications in list context
 * These notifications navigate to specific content pages
 * Uses shared ListNotificationBase with content-specific configuration
 */
export const ListContentNotification = ({
  notification,
  setIsNotificationsMenuOpen,
  prevNotification,
  getNotificationTypeHeadline,
  isLast = false,
}) => {
  const { _id, contentId, notificationType, course } = notification;
  const { _id: courseId } = course;

  const history = useHistory();
  const client = useApolloClient();
  const { isProcessing, createClickHandler } = useNotificationClick(
    _id,
    setIsNotificationsMenuOpen
  );

  // Compute notification link based on type - more efficient than useEffect + state
  const notificationLink = useMemo(() => {
    return getNotificationLink(notificationType, courseId, contentId);
  }, [notificationType, courseId, contentId]);

  const handleContentNavigation = () => {
    // For navigation-based notifications, clear cache and navigate programmatically
    if (notificationLink) {
      // Clear the specific teaching detail cache to force a fresh fetch
      client.cache.evict({
        fieldName: "courses",
        args: { _id: courseId },
      });
      client.cache.gc();
      // Navigate programmatically to ensure proper routing
      history.push(notificationLink);
    }
  };

  const handleNotificationClick = createClickHandler(
    handleContentNavigation,
    100
  );

  return (
    <ListNotificationBase
      notification={notification}
      prevNotification={prevNotification}
      getNotificationTypeHeadline={getNotificationTypeHeadline}
      onClick={handleNotificationClick}
      isProcessing={isProcessing}
      isLast={isLast}
      // Content-specific configuration
      notificationIcon={CONTENT_NOTIFICATION_CONFIG.getNotificationIcon(
        notificationType
      )}
      actionLabel={CONTENT_NOTIFICATION_CONFIG.getActionLabel(notificationType)}
      actionIcon={CONTENT_NOTIFICATION_CONFIG.getActionIcon()}
      actionColor={CONTENT_NOTIFICATION_CONFIG.actionColor}
      showTypeBadge={CONTENT_NOTIFICATION_CONFIG.showTypeBadge}
      typeBadgeIcon={CONTENT_NOTIFICATION_CONFIG.getTypeBadgeIcon(
        notificationType
      )}
    />
  );
};
